import {
  getAppUsageMetricsData,
  getAppUsageCountData,
  getCustomerList,
  getUsageMetricsData,
  getApiUsageMetricsData,
  getSSDUsageCountData,
  getVideoDimensionsUsageCountData,
  getNexaUsageSummary,
} from '@src/api/usageDashboard';
import { productInitFields } from '@utils/constants';
import { trackQueryError } from '@utils/sensor/apptracker';
import { useAtomValue, useSetAtom } from 'jotai';
import { cloneDeep, get, isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  appAccountsDetailsAtom,
  granularityAtom,
  intervalAtom,
  videoAccountsDetailsAtom,
  apiAccountsDetailsAtom,
  usageTableMetricList,
  usageMetricList,
} from '../atoms';
import { transformDataForChart, transformPlanLimitDataToMarkLineData } from './utils';
import {
  ACCOUNT_LIST_FOR_USAGE_DASHBOARD_QUERY_KEY,
  API_USAGE_METRIC_SERIES,
  APP_USAGE_METRIC_SERIES,
  EI_USAGE_METRIC_SERIES,
  NEXA_USAGE_SUMMARY_QUERY_KEY,
  USAGE_METRIC_SERIES_QUERY_KEY,
} from '../constant';

import { useApiUsageScope } from '../apiUsageScope';

const defaultMetricValue = { total: null, data: [] };
const defaultResponseValue = { totals: {}, data: {} };
const defaultTableResponseValue = { totals: {}, data: [] };

export function useGetCustomerList({ enabled = false }) {
  const setVideoAccountsDetails = useSetAtom(videoAccountsDetailsAtom);
  const setApiAccountsDetails = useSetAtom(apiAccountsDetailsAtom);
  const setAppAccountsDetails = useSetAtom(appAccountsDetailsAtom);
  const { isFetching, isError, data, refetch } = useQuery(
    [ACCOUNT_LIST_FOR_USAGE_DASHBOARD_QUERY_KEY, {}],
    () => {
      return getCustomerList({ params: { active: '1' } }).then((res) => {
        return res.data;
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled,
      staleTime: 1 * 60 * 1000, // to deduplicate requests
      placeholderData: null,
      onError: (err) => {
        trackQueryError({ type: ACCOUNT_LIST_FOR_USAGE_DASHBOARD_QUERY_KEY, err });
      },
    },
  );
  if (!isFetching && data) {
    setVideoAccountsDetails(cloneDeep(data.videoCustomerList));
    setAppAccountsDetails(cloneDeep(data.appCustomerList));
    setApiAccountsDetails(cloneDeep(data.apiCustomerList));
  }
  return { isFetching, isError, data, refetch };
}

export function useMakeReqBody(product, accountsDetails, isPlanLimit?) {
  const { filter: apiUsageFilter } = useApiUsageScope();
  const changedInterval: any = useAtomValue(intervalAtom);
  const granularity = useAtomValue(granularityAtom);
  const interval = `${changedInterval[0].format()}/${changedInterval[1].format()}`;
  const metrics = Object.keys(usageMetricList).filter(
    (metric) => usageMetricList[metric].product === product,
  );
  const tableMetrics = Object.keys(usageTableMetricList).filter(
    (metric) => usageTableMetricList[metric].product === product,
  );

  const selectedAccounts = accountsDetails
    .filter((cust) => cust.isSelected === true)
    .map((c) => c.customerId);

  const commonReqBody = {
    dataset: productInitFields[product].DATASET,
    filter: product === 'API' && !isPlanLimit ? apiUsageFilter : [],
    metrics,
    interval,
    granularity,
    isLiveMode: false,
    options: { withTotals: true },
    accountIds: selectedAccounts,
  };

  const reqBody: any = {
    queries: [
      {
        ...commonReqBody,
        type: 'timeseries',
        ...(isPlanLimit && { metricType: 'contract' }),
      },
    ],
  };
  const groupByReqBody: any = {
    queries: [
      {
        ...commonReqBody,
        metrics: tableMetrics,
        type: 'group-by',
        groupBy: ['customer_id'],
        sortBy: [],
      },
    ],
  };

  return { reqBody, groupByReqBody, metrics, interval };
}

export function useMakeReqBodyForCount(product, metrics, accountsDetails) {
  const changedInterval: any = useAtomValue(intervalAtom);
  const granularity = useAtomValue(granularityAtom);
  const interval = `${changedInterval[0].format()}/${changedInterval[1].format()}`;

  const selectedAccounts = accountsDetails
    .filter((cust) => cust.isSelected === true)
    .map((c) => c.customerId);

  const reqBody: any = {
    queries: [
      {
        type: 'timeseries',
        dataset: productInitFields[product].DATASET,
        filter: [],
        metrics,
        limit: 1,
        interval,
        granularity,
        isLiveMode: false,
        options: {
          withTotals: true,
        },
        accountIds: selectedAccounts,
      },
    ],
  };
  return { reqBody, interval };
}

export function useFetchVideoUsageMetric({
  enabled = false,
  product,
  metric = null,
  isPlanLimit = false,
}) {
  const videoAccountsDetails = useAtomValue(videoAccountsDetailsAtom);
  const { reqBody, metrics } = useMakeReqBody(product, videoAccountsDetails, isPlanLimit);
  const {
    isFetching,
    isError,
    data: response,
    refetch,
  } = useQuery(
    [USAGE_METRIC_SERIES_QUERY_KEY, EI_USAGE_METRIC_SERIES, reqBody],
    () => {
      return getUsageMetricsData(reqBody).then((res) => {
        let { data, totals } = get(res, ['data', '0'], []);
        if (isPlanLimit) {
          ({ data, totals } = transformPlanLimitDataToMarkLineData({
            data,
            totals,
          }));
        } else {
          ({ data, totals } = transformDataForChart({
            metrics,
            data,
            totals,
          }));
        }

        return { data, totals };
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled && videoAccountsDetails?.length > 0,
      staleTime: 1 * 60 * 1000, // to deduplicate requests
      placeholderData: defaultResponseValue,
      onError: (err) => {
        trackQueryError({ type: EI_USAGE_METRIC_SERIES, err });
      },
    },
  );
  const data = useMemo(() => {
    if (isEmpty(response)) {
      return metric ? defaultMetricValue : defaultResponseValue;
    }

    if (metric) {
      const { data, totals } = response;
      if (data[metric] == null || totals[metric] == null) return defaultMetricValue;
      return { total: totals[metric], data: data[metric] };
    }

    return response;
  }, [metric, response]);

  return { isFetching, isError, data, refetch };
}

export function useFetchAPIUsageMetric({
  enabled = false,
  product,
  metric = null,
  isPlanLimit = false,
}) {
  const apiAccountsDetails = useAtomValue(apiAccountsDetailsAtom);
  const { reqBody, metrics } = useMakeReqBody(product, apiAccountsDetails, isPlanLimit);
  const {
    isFetching,
    isError,
    data: response,
    refetch,
  } = useQuery(
    [USAGE_METRIC_SERIES_QUERY_KEY, API_USAGE_METRIC_SERIES, reqBody],
    () => {
      return getApiUsageMetricsData(reqBody).then((res) => {
        let { data, totals } = get(res, ['data', '0'], []);
        if (isPlanLimit) {
          ({ data, totals } = transformPlanLimitDataToMarkLineData({
            data,
            totals,
          }));
        } else {
          ({ data, totals } = transformDataForChart({
            metrics,
            data,
            totals,
          }));
        }
        return { data, totals };
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled && apiAccountsDetails?.length > 0,
      staleTime: 1 * 60 * 1000, // to deduplicate requests
      placeholderData: defaultResponseValue,
      onError: (err) => {
        trackQueryError({ type: API_USAGE_METRIC_SERIES, err });
      },
    },
  );
  const data = useMemo(() => {
    if (isEmpty(response)) {
      return metric ? defaultMetricValue : defaultResponseValue;
    }

    if (metric) {
      const { data, totals } = response;
      if (data[metric] == null || totals[metric] == null) return defaultMetricValue;
      return { total: totals[metric], data: data[metric] };
    }

    return response;
  }, [metric, response]);

  return { isFetching, isError, data, refetch };
}

export function useFetchAppUsageMetric({
  enabled = false,
  product,
  metric = null,
  isPlanLimit = false,
}) {
  const appAccountsDetails = useAtomValue(appAccountsDetailsAtom);
  const { reqBody, metrics } = useMakeReqBody(product, appAccountsDetails, isPlanLimit);
  const {
    isFetching,
    isError,
    data: response,
    error,
    refetch,
  } = useQuery(
    [USAGE_METRIC_SERIES_QUERY_KEY, APP_USAGE_METRIC_SERIES, reqBody],
    () => {
      return getAppUsageMetricsData(reqBody).then((res) => {
        let { data, totals } = get(res, ['data', '0'], []);
        if (isPlanLimit) {
          ({ data, totals } = transformPlanLimitDataToMarkLineData({
            data,
            totals,
          }));
        } else {
          ({ data, totals } = transformDataForChart({
            metrics,
            data,
            totals,
          }));
        }
        return { data, totals };
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled && appAccountsDetails?.length > 0,
      staleTime: 1 * 60 * 1000, // to deduplicate requests
      placeholderData: defaultResponseValue,
      onError: (err) => {
        trackQueryError({ type: APP_USAGE_METRIC_SERIES, err });
      },
    },
  );
  const data = useMemo(() => {
    if (isEmpty(response)) {
      return metric ? defaultMetricValue : defaultResponseValue;
    }

    if (metric) {
      const { data, totals } = response;
      if (data[metric] == null || totals[metric] == null) return defaultMetricValue;
      return { total: totals[metric], data: data[metric] };
    }

    return response;
  }, [metric, response]);

  return { isFetching, isError, data, error, refetch };
}

export function useFetchAppUsageCount({ enabled = false, product }) {
  const appAccountsDetails = useAtomValue(appAccountsDetailsAtom);
  const metrics = [
    'ecoTotalDimensions',
    // 'ecoTotalMetrics',
    // 'ecoTotalFlows',
    'ecoCustomDimensions',
    'ecoCustomMetrics',
  ];
  const { reqBody } = useMakeReqBodyForCount(product, metrics, appAccountsDetails);
  const {
    isFetching,
    isError,
    data = {},
    error,
    refetch,
  } = useQuery(
    ['app-count', reqBody],
    () => {
      return getAppUsageCountData(reqBody).then((res) => {
        const { totals: totalCount } = get(res, ['data', '0'], {});
        return {
          customDimensions: totalCount.ecoCustomDimensions,
          totalDimensions: totalCount.ecoTotalDimensions,
          metrics: totalCount.ecoCustomMetrics,
        };
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled && appAccountsDetails?.length > 0,
      staleTime: 1 * 60 * 1000, // to deduplicate requests
      placeholderData: { customDimensions: 0, totalDimensions: 0, metrics: 0 },
      onError: (err) => {
        trackQueryError({ type: 'app-count-error', err });
      },
    },
  );

  return { isFetching, isError, count: data, error, refetch };
}

export function useFetchSSDUsageCount({ enabled = false, product }) {
  const videoAccountsDetails = useAtomValue(videoAccountsDetailsAtom);
  const metrics = ['videoSsdDaily', 'videoSsdHourly'];
  const { reqBody } = useMakeReqBodyForCount(product, metrics, videoAccountsDetails);
  const {
    isFetching,
    isError,
    data = {},
    error,
    refetch,
  } = useQuery(
    ['ssd-count', reqBody],
    () => {
      return getSSDUsageCountData(reqBody).then((res) => {
        const { totals: totalCount } = get(res, ['data', '0'], {});

        // count: {SSDDaily: 42, SSDHourly: 102}
        return { SSDDaily: totalCount.videoSsdDaily, SSDHourly: totalCount.videoSsdHourly };
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled && videoAccountsDetails?.length > 0,
      staleTime: 1 * 60 * 1000, // to deduplicate requests
      placeholderData: { SSDDaily: 0, SSDHourly: 0 },
      onError: (err) => {
        trackQueryError({ type: 'ssd-count-error', err });
      },
    },
  );

  return { isFetching, isError, count: data, error, refetch };
}

export function useFetchVideoDimensionsUsageCount({ enabled = false, product }) {
  const videoAccountsDetails = useAtomValue(videoAccountsDetailsAtom);
  const metrics = ['videoTotalDimensions', 'videoCustomDimensions'];

  const { reqBody } = useMakeReqBodyForCount(product, metrics, videoAccountsDetails);
  const {
    isFetching,
    isError,
    data = {},
    error,
    refetch,
  } = useQuery(
    ['video-dimensions-count', reqBody],
    () => {
      return getVideoDimensionsUsageCountData(reqBody).then((res) => {
        const { totals: totalCount } = get(res, ['data', '0'], {});
        return {
          totalDimensions: totalCount.videoTotalDimensions,
          customDimensions: totalCount.videoCustomDimensions,
        };
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled && videoAccountsDetails?.length > 0,
      staleTime: 1 * 60 * 1000, // to deduplicate requests
      placeholderData: { totalDimensions: 0, customDimensions: 0 },

      onError: (err) => {
        trackQueryError({ type: 'video-dimensions-count-error', err });
      },
    },
  );
  return { isFetching, isError, count: data, error, refetch };
}

// Nexa usage summary — deduped union of selected accounts across all rails
import { getSelectedAccountIds, formatDateYMD } from './nexaUsageUtils';

const defaultNexaSummary = {
  total_tokens: 0,
  prompt_tokens: 0,
  output_tokens: 0,
  requests: 0,
  sessions: 0,
  active_users: 0,
};

export function useFetchNexaUsageCount({ enabled = false }) {
  const videoAccountsDetails = useAtomValue(videoAccountsDetailsAtom);
  const appAccountsDetails = useAtomValue(appAccountsDetailsAtom);
  const apiAccountsDetails = useAtomValue(apiAccountsDetailsAtom);
  const changedInterval: any = useAtomValue(intervalAtom);

  const accountIds = useMemo(
    () => getSelectedAccountIds(videoAccountsDetails, appAccountsDetails, apiAccountsDetails),
    [videoAccountsDetails, appAccountsDetails, apiAccountsDetails],
  );

  const from = changedInterval ? formatDateYMD(changedInterval[0]) : '';
  const to = changedInterval ? formatDateYMD(changedInterval[1]) : '';

  // usage_scope is required by IFS. This dashboard reports billable usage, so
  // it is pinned to external for every viewer; the three-way scope control
  // lives in nexa-client. IFS also pins the summary endpoint server-side.
  const reqBody = useMemo(
    () => ({ account_ids: accountIds, from, to, usage_scope: 'external' }),
    [accountIds, from, to],
  );

  const {
    isFetching,
    isError,
    data = defaultNexaSummary,
  } = useQuery(
    [NEXA_USAGE_SUMMARY_QUERY_KEY, reqBody],
    () => {
      return getNexaUsageSummary(reqBody).then((res) => {
        return get(res, ['data', 'totals'], defaultNexaSummary);
      });
    },
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled: enabled && accountIds.length > 0 && !!from && !!to,
      staleTime: 1 * 60 * 1000,
      placeholderData: defaultNexaSummary,
      onError: (err) => {
        trackQueryError({ type: NEXA_USAGE_SUMMARY_QUERY_KEY, err });
      },
    },
  );

  return { isFetching, isError, count: data };
}

// table view
function useFetchUsageMetrics({
  queryKey,
  fetchFunction,
  transformFunction,
  placeholderData,
  enabled = false,
  product,
  accountDetails,
}: {
  queryKey: string[];
  fetchFunction: (body: any) => Promise<any>;
  product: string;
  accountDetails: any[];
  transformFunction?: (res: any) => { data: any; totals: any };
  placeholderData?: any;
  enabled?: boolean;
}) {
  const { groupByReqBody, metrics } = useMakeReqBody(product, accountDetails);

  const {
    isFetching,
    isError,
    data: response,
    refetch,
  } = useQuery(
    [...queryKey, groupByReqBody],
    () =>
      fetchFunction(groupByReqBody).then((res) => {
        let { data, totals } = get(res, ['data', '0'], []);
        if (transformFunction) {
          ({ data, totals } = transformFunction({ metrics, data, totals }));
        }
        return { data, totals };
      }),
    {
      refetchOnWindowFocus: false,
      retry: false,
      enabled,
      staleTime: 1 * 60 * 1000, // to deduplicate requests
      placeholderData,
      onError: (err) => {
        trackQueryError({ type: queryKey, err });
      },
    },
  );

  return { isFetching, isError, data: response, refetch };
}

export function useFetchUsageMetricTable({
  enabled = false,
  product, // 'EI', 'APP', 'API'
}) {
  const videoAccountsDetails = useAtomValue(videoAccountsDetailsAtom);
  const apiAccountsDetails = useAtomValue(apiAccountsDetailsAtom);
  const appAccountsDetails = useAtomValue(appAccountsDetailsAtom);

  const accountDetails = {
    EI: videoAccountsDetails,
    APP: appAccountsDetails,
    API: apiAccountsDetails,
  };

  const {
    isFetching,
    isError,
    data: response,
    refetch,
  } = useFetchUsageMetrics({
    queryKey: [USAGE_METRIC_SERIES_QUERY_KEY, product],
    fetchFunction: getUsageMetricsData,
    product,
    accountDetails: accountDetails[product],
    placeholderData: defaultTableResponseValue,
    enabled,
  });

  return { isFetching, isError, data: response, refetch };
}
